import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { MeResponse } from "../lib/types.js";

export interface AuthMenuProps {
  me: MeResponse | null;
  loading: boolean;
}

/**
 * Right-side nav element on every page. When signed-out: a "Sign in" link.
 * When signed-in: a circular initial avatar (Field Day orange) that opens
 * a small dark popover with a single "Sign out" action. The popover styling
 * matches the rest of the Field Day surfaces — dark card, offset orange
 * shadow, white text on dark, hover swaps to orange accent.
 */
export function AuthMenu({ me, loading }: AuthMenuProps): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Click-outside-to-close.
  React.useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (loading || !me || !me.user) {
    return (
      <Link to="/signin" className="auth-link">
        Sign in
      </Link>
    );
  }

  const firstName = me.user.first_name?.trim();
  const displayName = firstName || me.user.email || "User";
  const initial = displayName.charAt(0).toUpperCase();

  function signOut() {
    setOpen(false);
    void fetch(
      `${(import.meta.env.VITE_API_BASE_URL ?? "https://api.sportsacademygames.com").replace(/\/$/, "")}/auth/logout`,
      { method: "POST", credentials: "include" },
    ).finally(() => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      navigate("/");
    });
  }

  return (
    <div className="auth-menu" ref={wrapRef}>
      <button
        type="button"
        className="avatar-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden="true" className="avatar-initial">
          {initial}
        </span>
        <span className="visually-hidden">{displayName} menu</span>
      </button>
      {open && (
        <div className="auth-pop" role="menu">
          {/* A teacher's home is /teach — never the consumer practice
              dashboard (placement, streaks, plans), which reads as a
              different product to a school buyer. */}
          {me.user.role === "teacher" ? (
            <Link
              to="/teach"
              role="menuitem"
              className="auth-pop-link"
              onClick={() => setOpen(false)}
            >
              My classes
            </Link>
          ) : (
            <Link
              to="/dashboard"
              role="menuitem"
              className="auth-pop-link"
              onClick={() => setOpen(false)}
            >
              Dashboard
            </Link>
          )}
          {me.user.staff_role && (
            <Link
              to="/admin"
              role="menuitem"
              className="auth-pop-link"
              onClick={() => setOpen(false)}
            >
              Admin
            </Link>
          )}
          <button type="button" role="menuitem" className="auth-pop-signout" onClick={signOut}>
            Sign out
            <span className="auth-pop-arrow" aria-hidden="true">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
